import React from "react";

import { Button } from "@replo/design-system/components/button/Button";
import { useIsMobile } from "@replo/design-system/hooks/use-mobile";
import twMerge from "@replo/design-system/utils/twMerge";
import { skipToken } from "@tanstack/react-query";
import { useCurrentProjectContext } from "@v2/contexts/CurrentProjectContext";
import { ProductCheckoutLink } from "@v2/features/products/components/ProductCheckoutLink";
import { ProductDeliveryEmail } from "@v2/features/products/components/ProductDeliveryEmail";
import { ProductEditForm } from "@v2/features/products/components/ProductEditForm";
import { ProductImageCard } from "@v2/features/products/components/ProductImageCard";
import { useProductFormEdit } from "@v2/features/products/hooks/useProductFormEdit";
import { ProductDeleteConfirmationModal } from "@v2/features/products/modals/ProductDeleteConfirmationModal";
import { StripeConnectIntegration } from "@v2/features/settings/shop/integrations/StripeConnectIntegration";
import { stripeConnectRouter } from "@v2/trpc/routes/stripeConnect";
import { useParams } from "react-router-dom";

export function ProductEditTab() {
  const { project } = useCurrentProjectContext();
  const { productId } = useParams<{ productId: string }>();
  const { creatingNewProduct, watch } = useProductFormEdit();
  const isMobile = useIsMobile();
  const [deleteModalOpen, setDeleteModalOpen] = React.useState(false);

  const { data: onboardingStatus, isLoading: isLoadingOnboardingStatus } =
    stripeConnectRouter.stripeConnectGetOnboardingStatus.useQuery(
      project?.id ? { projectId: project.id } : skipToken,
    );

  const isConnected =
    onboardingStatus?.isConnected && onboardingStatus?.onboardingCompleted;
  const checkoutMethod = watch("checkoutMethod");

  return (
    <div
      className={twMerge(
        "h-full overflow-y-auto no-scrollbar",
        isMobile
          ? "flex flex-col gap-4 px-4 py-6"
          : "flex justify-center gap-4",
      )}
    >
      {isMobile && <ProductImageCard />}
      <div className="flex flex-col gap-4">
        <ProductEditForm />
        <ProductCheckoutLink />
        {checkoutMethod === "replo" &&
          project &&
          !isLoadingOnboardingStatus && (
            <div className="w-full">
              {isConnected ? (
                <ProductDeliveryEmail />
              ) : (
                <StripeConnectIntegration variant="black-box" />
              )}
            </div>
          )}
        {!creatingNewProduct && productId && project && (
          <Button
            variant="tertiary"
            size="base"
            onClick={() => setDeleteModalOpen(true)}
            UNSAFE_className="text-rose"
          >
            Delete Product
          </Button>
        )}
      </div>
      {!isMobile && <ProductImageCard />}
      {productId && project && (
        <ProductDeleteConfirmationModal
          open={deleteModalOpen}
          onOpenChange={setDeleteModalOpen}
          productId={productId}
          projectId={project.id}
        />
      )}
    </div>
  );
}
